/**
 * The multiplayer off-turn waiting affordance's stage machine (multiplayer waiting-state UX
 * design § The stage machine, todo/multiplayer-waiting-state-ux). Server-state ingest is the
 * input, rendering is one of its outputs — NOT the render path: during PLAYING, mutation-driven
 * advances never touch the prompt zone at all (trick-mutations.js has no renderPromptZone call
 * site), so a render-hooked machine would arm once at the trick boundary and go deaf for the
 * feature's primary use case (round 2's blocking find).
 *
 * Two server-state ingest funnels call {@link noteServerState}, both in api.js (every applied
 * top-level payload passes through one of the two): the response/fetch accept path
 * (applyServerState) and the fetch/resync path (renderFetchedState, which passes `isResync:
 * true` — see {@link continueWait}'s own doc for why). Both see only top-level states with real
 * `seq` — nested `events[].next` snapshots (hardcoded `seq: 0`) never reach here, so there is no
 * nested-seq ambiguity to resolve. `api.js#showGameStart` calls {@link resetWaitingState}
 * directly — the deliberate quit renders the start screen without a state fetch reaching this
 * module otherwise.
 *
 * Import surface: this module is statically imported by the eager ingest funnel (api.js), so its
 * own static imports stay leaf-level (utils/i18n/constants/leaderboard/leaderboard-format — all
 * already on the eager graph). `overlay-shell.js`, `animation/button-plate.js`, `analytics.js`,
 * and `api.js` (the CTA's `showGameStart`) are dynamic-imported at use time, inside the
 * overlay-open path — cycle- and eager-weight-avoidance (the `menu-button.js -> game-menu.js`
 * precedent); the overlay opens minutes into a wait, where import latency is irrelevant.
 */
import { isViewerTurn } from "./utils.js";
import { t } from "./i18n.js";
import { WAITING } from "./constants.js";
import { formatRelativeDuration } from "./leaderboard/leaderboard-format.js";

// --- Injected callback (board-layout.js#setPlaceResultAssembly precedent) ---

/** @type {((state: object) => void)|null} Wired by prompt-ui.js at its own module top via
 *  initWaitingState — null is tolerated (a state can arrive before the game graph loads; the
 *  next ordinary render catches up). */
let rerenderPromptZone = null;

export function initWaitingState({ rerenderPromptZone: rerender } = {}) {
  rerenderPromptZone = rerender ?? null;
}

// --- Module state — one wait's worth of clocks, reset wholesale between waits ---

/** The latest ingested state, read by tick()'s own re-render call (which has no state parameter
 *  of its own — it fires off a timer, not an ingest). */
let currentState = null;

let applicable = false;
/** `${roundNumber}|${awaitedPosition}` — identifies a continuous stretch awaiting the SAME seat.
 *  Alone this under-keys "what is one wait" (the same human can act and be immediately
 *  re-awaited) — the activity-epoch fields below cover that finer distinction. */
let waitKey = null;
/** When the current wait (by waitKey) began — the LINE_DELAY_MS anchor. Never reset by an
 *  activity epoch: "the line persists uninterrupted" once it's showing. */
let waitStartMs = null;
/** Set once per wait's line, so the tick's own crossing re-render fires exactly once. */
let lineRenderTriggered = false;

/** When the current activity epoch began — the OVERLAY_GRACE_MS anchor, reset by an activity
 *  epoch without touching waitStartMs/lineRenderTriggered above. */
let epochStartMs = null;
/** Whether the overlay has already shown for the current epoch — reset alongside epochStartMs. */
let shownOnce = false;
/** Set when a tick found the overlay eligible but another overlay was open — the explicit +30s
 *  retry floor "Suppressed -> the timer re-arms (+30s) and retries" describes, since the natural
 *  eligibility formula alone would recompute the same already-past crossing and busy-loop. */
let overlaySuppressedRetryAtMs = null;

/** The idle-seconds anchor — updated only on a genuinely new snapshot (the seq gate), never
 *  overwritten by a null (unknown) value on a later one. */
let anchorIdleSeconds = null;
let anchorAtMs = null;
/** The highest state.seq this module has anchored against — the monotonic seq gate itself
 *  (state.seq > lastSeenSeq, continueWait). null only outside an applicable wait: resetAll()
 *  nulls it alongside applicable, and startNewWait() sets both together in the same synchronous
 *  call, so continueWait — reachable only past the `applicable` guard in noteServerState, which
 *  only startNewWait sets — never observes it null; no defensive null check needed there
 *  (CODING § no defensive null checks in internal code — trust callers). */
let lastSeenSeq = null;

/** The currently-open overlay's own dismiss, or null when this module has none open — the "one
 *  overlay at a time" bookkeeping this module needs on top of the shell's own latch, so a reset
 *  only ever tears down an overlay THIS module opened (never a pause menu or help page the
 *  player happens to be reading). */
let overlayDismiss = null;

let timerHandle = null;

/** The awaited seat's display name — rides PlayerDto, present on every state. */
function awaitedName(state) {
  return state.players?.find((p) => p.position === state.awaitedPosition)?.name;
}

function clearTimer() {
  if (timerHandle != null) {
    clearTimeout(timerHandle);
    timerHandle = null;
  }
}

/** The idle-seconds anchor projected to right now — null when never anchored (unknown recency). */
function effectiveIdleSNow() {
  if (anchorIdleSeconds == null) return null;
  return anchorIdleSeconds + (Date.now() - anchorAtMs) / 1000;
}

/** Starts a new activity epoch: dismisses whatever overlay this module has open (its "not here"
 *  premise just died — an epoch only ever starts because the awaited player acted, including the
 *  very first epoch of a brand new wait, where dismiss is a harmless no-op), and resets the
 *  epoch-scoped flags. Deliberately does NOT touch waitStartMs/lineRenderTriggered — the line
 *  itself persists uninterrupted across an epoch reset. */
function beginEpoch() {
  epochStartMs = Date.now();
  shownOnce = false;
  overlaySuppressedRetryAtMs = null;
  overlayDismiss?.();
  overlayDismiss = null;
}

/** The full reset both the not-applicable branch of noteServerState and resetWaitingState use. */
function resetAll() {
  clearTimer();
  overlayDismiss?.();
  overlayDismiss = null;
  applicable = false;
  waitKey = null;
  waitStartMs = null;
  lineRenderTriggered = false;
  epochStartMs = null;
  shownOnce = false;
  overlaySuppressedRetryAtMs = null;
  anchorIdleSeconds = null;
  anchorAtMs = null;
  lastSeenSeq = null;
}

/** The house quit path (api.js#showGameStart) — the deliberate quit renders the start screen
 *  without a state fetch reaching noteServerState, so this is the one explicit extra sweep. */
export function resetWaitingState() {
  resetAll();
  currentState = null;
}

function isApplicable(state) {
  return (
    state.multiplayerRun === true &&
    state.awaitedPosition != null &&
    !isViewerTurn(state)
  );
}

/** A continuous stretch awaiting a NEW seat (or the wait's very first ingest): anchors every
 *  clock fresh, including a first epoch (beginEpoch's dismiss is a harmless no-op here — nothing
 *  of this module's is open yet). Always worth an immediate re-render: the zone's content (who's
 *  awaited, and the reset-to-invisible line) genuinely changed. */
function startNewWait(state, key) {
  applicable = true;
  waitKey = key;
  waitStartMs = Date.now();
  lineRenderTriggered = false;
  beginEpoch();
  lastSeenSeq = state.seq;
  anchorIdleSeconds = state.awaitedIdleSeconds ?? null;
  anchorAtMs = Date.now();
  scheduleTick();
  rerenderPromptZone?.(state);
}

/** The same wait continues — re-anchors idle on a genuinely NEWER snapshot (the monotonic seq
 *  gate, todo/multiplayer-waiting-state-seq-gate-not-monotonic) OR unconditionally when `isResync`
 *  (renderFetchedState's ingest, sse-silent-connection-death-no-recovery). On the ORDINARY path
 *  (applyServerState, isResync false) the `>` comparison is in practice never false for a state
 *  that reaches here at all — api.js's own claimSeq already refuses a `state.seq` that doesn't
 *  exceed its own prior watermark before applyServerState ever calls noteServerState, so this
 *  module's `>` is a second, cheap enforcement of an invariant claimSeq already guarantees on that
 *  path, not new protection there (review round, sse-silent-connection-death-no-recovery). It's
 *  genuinely load-bearing only cross-path: an applicable RESYNC (renderFetchedState) landing with
 *  a stale/lower seq after a fresher applyServerState-delivered state already anchored higher —
 *  exactly todo/multiplayer-waiting-state-seq-gate-not-monotonic's own scenario.
 *
 *  `isResync` unconditionally re-anchors — including in that exact stale-resync scenario — because
 *  a resync's own seq can ALSO legitimately be lower for an unrelated reason: GameStreamRegistry's
 *  seq counter is in-memory and game-scoped, restarting at 0 for the same game on a server restart
 *  while the game itself (and this wait's key) survives via LiveGameStore's durability, so a wait
 *  spanning a restart must still re-anchor on the post-restart snapshot. Nothing on the wire (the
 *  DTO carries a bare `seq`, no incarnation/epoch discriminator — verified against
 *  GameStateDto.java) lets this module tell "genuine restart" apart from "stale race" — the SAME
 *  ambiguity push-stream.js#rebaseWatermark's own bidirectional carve-out already accepts for the
 *  board's own watermark (its doc has the mechanism; this module's clock re-anchors on the exact
 *  same `state` object, in the same `renderFetchedState` call, so it can never show something the
 *  board itself doesn't). Reviewed and accepted bound on the race this reopens: a stale resync's
 *  return-leg latency has to clear `WAITING.IDLE_DROP_TOLERANCE_S` before `anchorIdleIfKnown`
 *  begins a spurious activity epoch, and the effect is a ~`WAITING.OVERLAY_GRACE_MS`-bounded
 *  spurious dismiss-then-reopen of an already-open overlay, not the indefinite freeze the
 *  same-game-restart gap produces when this carve-out is absent — a deliberate trade-off, not an
 *  oversight; a real fix needs a wire-protocol incarnation/epoch id, out of scope here.
 *
 *  Without the `isResync` carve-out, neither a tick's own repaint, a re-ingested duplicate, NOR an
 *  out-of-order/stale ORDINARY ingest can re-anchor idle and postpone escalation. Never itself
 *  worth a re-render: the zone's content doesn't change ingest to ingest here, only (later) via
 *  the tick's own line/overlay crossings. */
function continueWait(state, isResync) {
  if (isResync || state.seq > lastSeenSeq) {
    lastSeenSeq = state.seq;
    anchorIdleIfKnown(state.awaitedIdleSeconds);
  }
  scheduleTick();
}

/** A null idleSeconds (unknown) never overwrites a known anchor. A LOWER known value than the
 *  current effective idle means the awaited player acted since the last anchor — an activity
 *  epoch, under the same wait key ("what is one wait" answered: a continuous stretch awaiting
 *  the same seat WITHOUT observed activity). Gate-verdict tweak: the drop must clear
 *  WAITING.IDLE_DROP_TOLERANCE_S before it counts — idleSeconds is the server's whole-second
 *  floor, `before` a continuous float extrapolation, so a fresh floored reading can land under
 *  `before` from truncation alone plus ingest lag, with no real activity (constants.js's `// why:`
 *  has the derivation). No true positive is lost: the only drop that matters in practice is one
 *  large enough to dismiss an ALREADY-OPEN overlay, and that overlay only opens once idle has
 *  reached OFFLINE_AFTER_S (300s) — so every drop that could visibly dismiss one is hundreds of
 *  seconds, not a couple. A genuine same-seat re-pause under 2s apart (reachable mid-declaration)
 *  can in principle miss its own epoch, but idle is already ~0 there and no overlay has had time
 *  to open — nothing observable is lost. */
function anchorIdleIfKnown(idleSeconds) {
  if (idleSeconds == null) return;
  const before = effectiveIdleSNow();
  if (before != null && idleSeconds < before - WAITING.IDLE_DROP_TOLERANCE_S) {
    beginEpoch();
  }
  anchorIdleSeconds = idleSeconds;
  anchorAtMs = Date.now();
}

/**
 * The stage machine's one input. On non-applicable (viewer's own turn, FINISHED, phase == null —
 * which also covers the involuntary 404/session-loss return to the start screen, since a sparse
 * DTO's awaitedPosition is always null) clears the timer, dismisses any overlay this module has
 * open, and resets every flag. Drives the injected re-render itself exactly when an ingest
 * changes what the zone should display — applicability flipping either way, or (staying
 * applicable) the awaited seat itself changing — never on an ordinary same-wait advance, which
 * would otherwise flicker the zone on every uninvolved card play.
 *
 * `isResync: true` (renderFetchedState's own call, api.js) marks this state as arriving via the
 * fetch/resync authority rather than the ordinary applyServerState funnel — continueWait's own
 * doc explains why that ingest is allowed to re-anchor unconditionally, even on a same-wait
 * continuation whose seq isn't newer.
 */
export function noteServerState(state, { isResync = false } = {}) {
  currentState = state;
  if (state == null) return;

  if (!isApplicable(state)) {
    const wasApplicable = applicable;
    resetAll();
    if (wasApplicable) rerenderPromptZone?.(state);
    return;
  }

  const key = `${state.roundNumber}|${state.awaitedPosition}`;
  if (!applicable || key !== waitKey) {
    startNewWait(state, key);
    return;
  }
  continueWait(state, isResync);
}

/** For promptContentKey's discriminator — deliberately impure (reads the module's clocks, not a
 *  state parameter): "what the zone would render now", not a pure state signature. */
export function waitingLineVisible() {
  return (
    applicable &&
    waitStartMs != null &&
    Date.now() - waitStartMs >= WAITING.LINE_DELAY_MS
  );
}

/** Renders the line iff the line delay has crossed — called by renderPrompts' off-turn branch.
 *  The box's geometry is reserved unconditionally while applicable (prompt-ui.geometry.js); this
 *  only decides whether the text itself has earned its keep yet (DESIGN 5, reveal-when-earned). */
export function renderWaitingState(container, state) {
  if (!waitingLineVisible()) return;
  const name = awaitedName(state);
  if (name == null) return;
  const box = document.createElement("div");
  box.className = "waiting-state";
  const line = document.createElement("span");
  line.className = "waiting-line";
  line.textContent = t("awaitedPlayerTurn", name);
  box.appendChild(line);
  container.appendChild(box);
}

// --- The overlay ---

function buildRecencyLine(effectiveIdleS) {
  if (effectiveIdleS == null) return null; // unknown recency — omitted per the design's blockquote
  const line = document.createElement("span");
  line.className = "confirm-dialog-body";
  line.textContent = t(
    "waitingOfflineRecency",
    formatRelativeDuration(effectiveIdleS * 1000),
  );
  return line;
}

/** Builds and wires the CTA synchronously (armButtonPlate arrives a tick later, fire-and-forget —
 *  the static `.btn-plate` CSS already gives it a resting look immediately) so the overlay never
 *  opens without its one action already clickable. */
function buildWaitingCta(dismiss) {
  const cta = document.createElement("button");
  cta.className = "btn btn-primary btn-plate waiting-cta";
  cta.textContent = t("waitingContinue");
  cta.addEventListener("click", () => {
    dismiss();
    import("./analytics.js").then(({ track }) => track("waiting-release-used"));
    import("./api.js").then(({ showGameStart }) => showGameStart());
  });
  import("./animation/button-plate.js").then(({ armButtonPlate }) =>
    armButtonPlate(cta),
  );
  return cta;
}

/** Appends the headline/recency/note/CTA content built above onto the freshly opened `panel`.
 *  Review finding: this is one of the four header-less overlays overlay-shell.js's `header`
 *  option reserves, so it must self-wire its own accessible name — the shell's
 *  own doc names the pattern (a caller may set `aria-labelledby` on the returned panel instead of
 *  opting into `header`). No per-instance id counter needed (unlike confirm-dialog.js's
 *  `dialogIdCounter`): this module's own "one overlay at a time" latch (`overlayDismiss`) means at
 *  most one waiting-overlay instance is ever live. */
function fillWaitingOverlay(panel, name, effectiveIdleS, dismiss) {
  const headline = document.createElement("h2");
  headline.className = "confirm-dialog-title";
  headline.id = "waiting-overlay-title";
  headline.textContent = t("waitingOfflineHeadline", name);
  panel.appendChild(headline);
  panel.setAttribute("aria-labelledby", headline.id);

  const recency = buildRecencyLine(effectiveIdleS);
  if (recency) panel.appendChild(recency);

  const note = document.createElement("span");
  note.className = "confirm-dialog-body";
  note.textContent = t("waitingOfflineNote");
  panel.appendChild(note);

  const actions = document.createElement("div");
  actions.className = "confirm-dialog-actions";
  actions.appendChild(buildWaitingCta(dismiss));
  panel.appendChild(actions);
}

/** Opens the release overlay for `state`, or does nothing (returning false) when the shell
 *  already has a different overlay open — a timer-fired modal must never tear down a pause menu
 *  or help page the player is reading (overlay-shell.js's supersede-on-open is unconditional, so
 *  the check has to happen here, before ever calling openVellumOverlay). */
async function openWaitingOverlay(state, effectiveIdleS) {
  const name = awaitedName(state);
  if (name == null) return false;
  // Review finding: this module's own first-ever overlay open is the one await below that's a
  // real macrotask (overlay-shell.js isn't on the eager graph, so the import is a fetch+parse, not
  // a cache hit) — an ingest can land in that window. Capturing the wait/epoch identity BEFORE the
  // await and rechecking BOTH after (not just `applicable`) closes every shape of that race, not
  // only the wait ending outright: a same-key idle-drop starts a fresh epoch without touching
  // `applicable` (beginEpoch), and a same-tick different-seat wait replaces `waitKey` without ever
  // passing through non-applicable — either would otherwise open with `state`/`effectiveIdleS`
  // captured for a wait/epoch that's no longer the live one.
  const openedForKey = waitKey;
  const openedForEpoch = epochStartMs;
  const { openVellumOverlay, isOverlayOpen } =
    await import("./overlay-shell.js");
  if (
    !applicable ||
    waitKey !== openedForKey ||
    epochStartMs !== openedForEpoch ||
    isOverlayOpen()
  ) {
    return false;
  }

  const { panel, dismiss } = openVellumOverlay("waiting-overlay-panel", {
    // No corner × here: this overlay's own CTA is its close, and it arrives unbidden on a timer —
    // its dismissal is the answer to a question, not a chrome affordance (the shell's
    // `closeButton` paragraph).
    closeButton: false,
    onClose: () => {
      overlayDismiss = null;
    },
  });
  overlayDismiss = dismiss;
  fillWaitingOverlay(panel, name, effectiveIdleS, dismiss);
  return true;
}

// --- The tick — the single armed timer for the earliest future crossing ---

function nextOverlayEligibleAtMs() {
  const graceAt = epochStartMs + WAITING.OVERLAY_GRACE_MS;
  const offlineAt =
    anchorIdleSeconds != null
      ? anchorAtMs + (WAITING.OFFLINE_AFTER_S - anchorIdleSeconds) * 1000
      : waitStartMs + WAITING.OFFLINE_AFTER_S * 1000;
  const naturalAt = Math.max(graceAt, offlineAt);
  return overlaySuppressedRetryAtMs != null
    ? Math.max(naturalAt, overlaySuppressedRetryAtMs)
    : naturalAt;
}

function overlayEligibleNow() {
  const now = Date.now();
  if (now - epochStartMs < WAITING.OVERLAY_GRACE_MS) return false;
  const idle = effectiveIdleSNow();
  if (idle != null) return idle >= WAITING.OFFLINE_AFTER_S;
  return (now - waitStartMs) / 1000 >= WAITING.OFFLINE_AFTER_S; // unknown-recency fallback
}

/** Arms the one timer for whichever of {line crossing, overlay crossing} is next — a no-op while
 *  not applicable (noteServerState's reset path already cleared any prior timer) or once both
 *  crossings this wait/epoch cares about have already fired. */
function scheduleTick() {
  clearTimer();
  if (!applicable) return;
  const now = Date.now();
  const lineAt = lineRenderTriggered
    ? null
    : waitStartMs + WAITING.LINE_DELAY_MS;
  const overlayAt = shownOnce ? null : nextOverlayEligibleAtMs();
  const candidates = [lineAt, overlayAt].filter((ms) => ms != null);
  if (candidates.length === 0) return;
  timerHandle = setTimeout(tick, Math.max(0, Math.min(...candidates) - now));
}

/** The tick's own re-render/overlay-open side effects — never a render side effect (no artificial
 *  content-key bump, no identical-line rebuild): the line's crossing fires the injected callback
 *  exactly once per wait, and the overlay open is this timer's own side effect alone. */
async function tick() {
  timerHandle = null;
  if (!applicable || currentState == null) return;

  if (!lineRenderTriggered && waitingLineVisible()) {
    lineRenderTriggered = true;
    rerenderPromptZone?.(currentState);
  }

  if (!shownOnce && overlayEligibleNow()) {
    const opened = await openWaitingOverlay(currentState, effectiveIdleSNow());
    if (opened) {
      shownOnce = true;
    } else {
      overlaySuppressedRetryAtMs = Date.now() + WAITING.OVERLAY_GRACE_MS;
    }
  }

  scheduleTick();
}
